import {
    MemberPreferredCommunicationChannel,
    type MemberPreferredContactSlot,
} from "@albertine/types";
import { filter, isTruthy, join } from "remeda";
import { PREFERRED_CHANNEL_LABELS } from "@/utils/channel.util";
import { formatMemberPhoneNumber } from "@/utils/phone.util";
import type { ContactDetails, TopCity } from "./data/profile";

export function formatInterestsDisplay(personal: {
    interests?: string;
    loves?: string;
    hates?: string;
}): string | undefined {
    return joinPresent(
        [
            personal.interests?.trim(),
            personal.loves?.trim()
                ? `Really loves: ${personal.loves.trim()}`
                : undefined,
            personal.hates?.trim()
                ? `Really hates: ${personal.hates.trim()}`
                : undefined,
        ],
        "\n",
    );
}

export function formatAddress(parts: {
    address?: string | null;
    postalCode?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
}): string | undefined {
    const { address, postalCode, city, country, state } = parts;
    const result: string[] = [];

    if (address) {
        result.push(address);
    }

    if (postalCode && city) {
        result.push(`${postalCode} ${city}`);
    } else if (postalCode) {
        result.push(postalCode);
    } else if (city) {
        result.push(city);
    }

    if (state) {
        result.push(state);
    }

    if (country) {
        result.push(country);
    }

    return result.length !== 0 ? result.join(",\n") : undefined;
}

export function listMissingLabels(
    entries: { label: string; present: boolean }[],
): string[] | undefined {
    const missing = entries
        .filter((entry) => !entry.present)
        .map((entry) => entry.label);
    return missing.length ? missing : undefined;
}

const CHANNEL_CONTACT_FIELDS: Partial<
    Record<
        MemberPreferredCommunicationChannel,
        readonly ["phoneNumber" | "email", "secondaryPhone" | "secondaryEmail"]
    >
> = {
    whatsapp: ["phoneNumber", "secondaryPhone"],
    "phone-call": ["phoneNumber", "secondaryPhone"],
    email: ["email", "secondaryEmail"],
};

export function listChannelContactOptions(
    channel: string,
    contacts: Pick<
        ContactDetails,
        "phoneNumber" | "secondaryPhone" | "email" | "secondaryEmail"
    >,
): { id: MemberPreferredContactSlot; label: string }[] {
    const preferred = MemberPreferredCommunicationChannel.safeParse(channel);
    if (!preferred.success) return [];

    const fields = CHANNEL_CONTACT_FIELDS[preferred.data];
    if (!fields) return [];

    const [primaryField, secondaryField] = fields;
    const primary = contacts[primaryField]?.trim() ?? "";
    const secondary = contacts[secondaryField]?.trim() ?? "";
    if (!primary || !secondary) return [];

    return [
        { id: "primary", label: primary },
        { id: "secondary", label: secondary },
    ];
}

export function formatPreferredCommunicationChannel(
    contact: ContactDetails,
): string | undefined {
    if (!contact.preferredCommunicationChannel) return undefined;

    const label = PREFERRED_CHANNEL_LABELS[contact.preferredCommunicationChannel];
    const choices = listChannelContactOptions(
        contact.preferredCommunicationChannel,
        contact,
    );
    if (!choices.length) return label;

    const chosen =
        choices.find((choice) => choice.id === contact.channelContact) ?? choices[0];
    const usesPhone =
        contact.preferredCommunicationChannel === "whatsapp" ||
        contact.preferredCommunicationChannel === "phone-call";
    const chosenLabel = usesPhone
        ? formatMemberPhoneNumber(chosen.label)
        : chosen.label;

    return joinPresent([label, chosenLabel], "\n");
}

export function formatTopCities(cities: TopCity[] | undefined): string | undefined {
    return joinPresent(
        cities?.map((city) => joinPresent([city.city, city.country], ", ")) ?? [],
        "\n",
    );
}

function joinPresent(
    parts: (string | undefined)[],
    separator: string,
): string | undefined {
    const present = filter(parts, isTruthy);
    return present.length ? join(present, separator) : undefined;
}
